/**
 * PortalVerifyInvoice — Client-facing invoice authenticity check.
 *
 * No camera. The client enters the application number (or scans a QR which
 * brings them here with ref+hash pre-filled in the URL) and the page shows
 * a status badge + the original invoice values pulled live from the DB.
 *
 *   • valid      — green badge "فاتورة أصلية ومعتمدة"
 *   • tampered   — red badge   "⚠️ فاتورة معدّلة خارج النظام"
 *   • not_found  — orange      "لم يتم العثور على الفاتورة"
 *   • cancelled  — red         "ملغاة"
 *   • refunded   — gold        "مستردّة"
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ShieldOff, Search, Loader2 } from "lucide-react";
import axios from "axios";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNum } from "@/lib/format";

const STATUS_META = {
  valid: {
    icon: ShieldCheck,
    color: "#16a34a",
    bg: "rgba(22,163,74,0.12)",
    label_ar: "فاتورة أصلية ومعتمدة",
    label_en: "Authentic Invoice — Verified",
    label_ur: "تصدیق شدہ اصل انوائس",
  },
  tampered: {
    icon: ShieldAlert,
    color: "#dc2626",
    bg: "rgba(220,38,38,0.12)",
    label_ar: "فاتورة معدّلة خارج النظام",
    label_en: "Possible Tampering Detected",
    label_ur: "ممکنہ چھیڑ چھاڑ کا پتہ چلا",
  },
  cancelled: {
    icon: ShieldOff,
    color: "#dc2626",
    bg: "rgba(220,38,38,0.10)",
    label_ar: "فاتورة ملغاة",
    label_en: "Cancelled",
    label_ur: "منسوخ شدہ",
  },
  refunded: {
    icon: ShieldOff,
    color: "#d4af37",
    bg: "rgba(212,175,55,0.12)",
    label_ar: "فاتورة مستردّة",
    label_en: "Refunded",
    label_ur: "واپس کر دی گئی",
  },
  not_found: {
    icon: ShieldOff,
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.10)",
    label_ar: "لم يتم العثور على الفاتورة",
    label_en: "Invoice not found",
    label_ur: "انوائس نہیں ملا",
  },
};

export default function PortalVerifyInvoice() {
  const { lang, theme, dir } = useI18n();
  const [params, setParams] = useSearchParams();
  const [refInput, setRefInput] = useState(params.get("ref") || params.get("application_number") || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const search = async (q) => {
    if (!q?.trim()) { setErr(lang === "ar" ? "أدخل رقم المعاملة أو الفاتورة" : "Enter an invoice or application number"); return; }
    setBusy(true); setErr(""); setResult(null);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const isInv = q.toUpperCase().startsWith("INV-");
      const param = isInv ? `ref=${encodeURIComponent(q)}` : `application_number=${encodeURIComponent(q)}`;
      const hash = params.get("hash");
      const url = `${base}/api/portal/verify-invoice?${param}${hash ? `&hash=${encodeURIComponent(hash)}` : ""}`;
      const { data } = await axios.get(url);
      setResult(data);
    } catch (e) {
      setErr(e.message || "Network error");
    } finally {
      setBusy(false);
    }
  };

  // Auto-search if landed via QR link with ?ref=… or ?application_number=…
  useEffect(() => {
    const auto = params.get("ref") || params.get("application_number");
    if (auto) search(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = (ar, en, ur) => lang === "ur" ? ur : lang === "en" ? en : ar;
  const css = makeStyles(theme);

  return (
    <div dir={dir} style={css.page} data-testid="portal-verify-page">
      <div style={css.card}>
        <div style={css.head}>
          <div style={css.headIcon}><ShieldCheck size={26} strokeWidth={2.2} /></div>
          <div>
            <div style={css.title}>{t("التحقق من الفاتورة", "Invoice Verification", "انوائس کی تصدیق")}</div>
            <div style={css.sub}>
              {t(
                "تأكد من أصالة فاتورتك. أدخل رقم المعاملة أو رقم الفاتورة المطبوع، وسنُظهر لك البيانات الأصلية من النظام.",
                "Confirm your invoice is authentic. Enter your application number or invoice number to see the original values from our system.",
                "اپنی انوائس کی صداقت کی تصدیق کریں۔",
              )}
            </div>
          </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); setParams({ ref: refInput }); search(refInput); }} style={css.form}>
          <div style={css.inputRow}>
            <Search size={16} strokeWidth={2.2} style={{ color: theme.textMuted }} />
            <input
              value={refInput}
              onChange={(e) => setRefInput(e.target.value.toUpperCase())}
              placeholder={t("VTA-26-XXXX أو INV-2026-000001", "VTA-26-XXXX or INV-2026-000001", "VTA-26-XXXX")}
              style={css.input}
              dir="ltr"
              data-testid="verify-input"
            />
          </div>
          <button type="submit" disabled={busy} style={css.btnGold} data-testid="verify-search-btn">
            {busy ? <Loader2 size={16} className="vta-spin" /> : <Search size={16} strokeWidth={2.4} />}
            {busy ? t("جاري التحقق…", "Verifying…", "تصدیق ہو رہا ہے…") : t("تحقق", "Verify", "تصدیق")}
          </button>
        </form>

        {err && <div style={css.err} data-testid="verify-error">{err}</div>}

        {result && (
          <ResultCard result={result} t={t} theme={theme} lang={lang} />
        )}
      </div>

      <style>{`.vta-spin{animation:vta-spin 1s linear infinite}@keyframes vta-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ResultCard({ result, t, theme, lang }) {
  const meta = STATUS_META[result.status] || STATUS_META.not_found;
  const Icon = meta.icon;
  const label = lang === "ur" ? meta.label_ur : lang === "en" ? meta.label_en : meta.label_ar;
  return (
    <div data-testid={`verify-result-${result.status}`}>
      <div style={{
        background: meta.bg, color: meta.color,
        borderRadius: 14, padding: "16px 18px", display: "flex",
        alignItems: "center", gap: 12, fontWeight: 900, fontSize: 16,
        marginTop: 18, border: `2px solid ${meta.color}`,
      }}>
        <Icon size={24} strokeWidth={2.4} />
        <span data-testid="verify-status-label">{label}</span>
      </div>
      {(result.status === "valid" || result.status === "tampered" || result.status === "cancelled" || result.status === "refunded") && (
        <div style={{ marginTop: 16, padding: 16, background: theme.surfaceSubtle, borderRadius: 12, border: `1px solid ${theme.borderSoft}` }}>
          <Row label={t("رقم المعاملة", "Application No.", "ایپلیکیشن نمبر")} value={result.application_number} ltr />
          <Row label={t("رقم الفاتورة", "Invoice No.", "انوائس نمبر")} value={result.invoice_ref_number} ltr />
          <Row label={t("اسم العميل", "Client Name", "کلائنٹ کا نام")} value={result.client_name} />
          <Row label={t("الدولة", "Country", "ملک")} value={result.country} />
          <Row label={t("نوع التأشيرة", "Visa Type", "ویزا کی قسم")} value={result.visa_type} />
          <Row label={t("الإجمالي", "Total", "کل")} value={`${fmtNum(result.final_total)} ${result.currency}`} ltr bold />
          {result.discount_amount > 0 && (
            <Row label={t("الخصم", "Discount", "رعایت")} value={`${fmtNum(result.discount_amount)} ${result.currency}`} ltr />
          )}
          <Row label={t("حالة الفاتورة", "Invoice Status", "انوائس کی حالت")} value={result.invoice_status} />
          {result.issued_at && (
            <Row label={t("تاريخ الإصدار", "Issued At", "اجرا کی تاریخ")} value={new Date(result.issued_at).toLocaleString("en-GB", { timeZone: "Asia/Riyadh", hour12: false })} ltr />
          )}
          {result.revision > 0 && (
            <Row label={t("رقم المراجعة", "Revision", "نظرثانی")} value={`Rev.${result.revision}`} ltr />
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, ltr, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dashed rgba(0,0,0,0.06)", gap: 8 }}>
      <span style={{ fontSize: 12, opacity: 0.7 }}>{label}</span>
      <span style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 900 : 600, direction: ltr ? "ltr" : "rtl" }}>{value || "—"}</span>
    </div>
  );
}

function makeStyles(theme) {
  return {
    page: { padding: 18, minHeight: "100vh", background: theme.bg, color: theme.text },
    card: { maxWidth: 560, margin: "0 auto", background: theme.surface, padding: 22, borderRadius: 16, border: `1px solid ${theme.borderSoft}` },
    head: { display: "flex", gap: 14, marginBottom: 18 },
    headIcon: { background: theme.goldGradient, color: "#0a0a0a", width: 50, height: 50, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    title: { fontSize: 18, fontWeight: 900, color: theme.gold, marginBottom: 4 },
    sub: { fontSize: 12, color: theme.textMuted, lineHeight: 1.7 },
    form: { display: "flex", gap: 8, marginTop: 8 },
    inputRow: { flex: 1, display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: theme.surfaceSubtle, borderRadius: 10, border: `1px solid ${theme.borderSoft}` },
    input: { flex: 1, border: "none", background: "transparent", color: theme.text, fontSize: 14, fontFamily: "monospace", outline: "none" },
    btnGold: { background: theme.gold, color: "#0a0a0a", border: "none", padding: "10px 18px", borderRadius: 10, fontWeight: 900, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 },
    err: { background: "rgba(220,38,38,0.10)", color: "#dc2626", padding: 10, borderRadius: 8, fontSize: 12, marginTop: 12 },
  };
}
